import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet';
import {
  FUNDING_RIGHT_DRAWER_CLASS,
} from './fundingDrawerUi';
import { Button } from '../../ui/button';
import TimeDurationSelect from './TimeDurationSelect';

const emptyProfileForm = () => ({
  owner_name: '',
  ownership_percentage: 100,
  ssn: '',
  dob: '',
  full_ein: '',
  business_legal_name: '',
  business_start_date: '',
  employer_name: '',
  time_live_in_house_years: 0,
  time_live_in_house_months: 0,
  time_working_at_years: 0,
  time_working_at_months: 0,
  personal_income: '',
  business_income: '',
  personal_address: '',
  business_address: '',
  notes: '',
  is_primary: false,
});

const FundingIdentityProfileDrawer = ({
  open,
  onOpenChange,
  profile,
  onSave,
  saving = false,
  title = 'Add Identity & Business',
}) => {
  const [form, setForm] = useState(emptyProfileForm());

  useEffect(() => {
    if (!open) return;
    if (profile) {
      setForm({
        owner_name: profile.ownerName || '',
        ownership_percentage: profile.ownershipPercentage ?? 100,
        ssn: '',
        dob: profile.dob ? String(profile.dob).slice(0, 10) : '',
        full_ein: profile.fullEin || '',
        business_legal_name: profile.businessLegalName || '',
        business_start_date: profile.businessStartDate ? String(profile.businessStartDate).slice(0, 10) : '',
        employer_name: profile.employerName || '',
        time_live_in_house_years: profile.timeLiveInHouseYears ?? 0,
        time_live_in_house_months: profile.timeLiveInHouseMonths ?? 0,
        time_working_at_years: profile.timeWorkingAtYears ?? 0,
        time_working_at_months: profile.timeWorkingAtMonths ?? 0,
        personal_income: profile.personalIncome ?? '',
        business_income: profile.businessIncome ?? '',
        personal_address: profile.personalAddress || '',
        business_address: profile.businessAddress || '',
        notes: profile.notes || '',
        is_primary: Boolean(profile.isPrimary),
      });
    } else {
      setForm(emptyProfileForm());
    }
  }, [open, profile]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!form.owner_name?.trim()) {
      toast.error('Owner name is required');
      return;
    }
    const payload = {
      ...form,
      ownership_percentage: form.ownership_percentage != null ? Number(form.ownership_percentage) : null,
      personal_income: form.personal_income !== '' ? Number(form.personal_income) : null,
      business_income: form.business_income !== '' ? Number(form.business_income) : null,
      ssn: form.ssn || undefined,
    };
    await onSave?.(payload);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        className={`${FUNDING_RIGHT_DRAWER_CLASS} w-full overflow-y-auto sm:max-w-lg`}
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 grid gap-3">
          {[
            ['owner_name', 'Owner Name', 'text'],
            ['ownership_percentage', 'Ownership %', 'number'],
            ['ssn', 'SSN', 'text'],
            ['dob', 'DOB', 'date'],
            ['full_ein', 'Full EIN', 'text'],
            ['business_legal_name', 'Business Legal Name', 'text'],
            ['business_start_date', 'Business Start Date', 'date'],
            ['employer_name', 'Employer', 'text'],
          ].map(([key, label, type]) => (
            <label key={key} className="text-xs text-[var(--fg-muted)]">
              {label}
              <input
                type={type}
                value={form[key] ?? ''}
                onChange={(e) => setField(key, e.target.value)}
                className="mt-1 h-10 w-full rounded-xl border border-[var(--fg-line)] px-3 text-sm"
              />
            </label>
          ))}
          <TimeDurationSelect
            label="Time Live in House"
            years={form.time_live_in_house_years}
            months={form.time_live_in_house_months}
            onYearsChange={(v) => setField('time_live_in_house_years', v)}
            onMonthsChange={(v) => setField('time_live_in_house_months', v)}
          />
          <TimeDurationSelect
            label="Time Working At"
            years={form.time_working_at_years}
            months={form.time_working_at_months}
            onYearsChange={(v) => setField('time_working_at_years', v)}
            onMonthsChange={(v) => setField('time_working_at_months', v)}
          />
          {[
            ['personal_income', 'Personal Income'],
            ['business_income', 'Business Income'],
            ['personal_address', 'Personal Address'],
            ['business_address', 'Business Address'],
          ].map(([key, label]) => (
            <label key={key} className="text-xs text-[var(--fg-muted)]">
              {label}
              <input
                value={form[key] ?? ''}
                onChange={(e) => setField(key, e.target.value)}
                className="mt-1 h-10 w-full rounded-xl border border-[var(--fg-line)] px-3 text-sm"
              />
            </label>
          ))}
          <label className="text-xs text-[var(--fg-muted)]">
            Notes
            <textarea
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-xl border border-[var(--fg-line)] px-3 py-2 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => setField('is_primary', e.target.checked)}
            />
            Mark as Primary
          </label>
          <Button className="rounded-xl bg-[var(--fg-accent)]" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Save Profile'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FundingIdentityProfileDrawer;
